import * as THREE from "three";
import type { TransactionEvent } from "../data/types";
import { buildArcCurveFlat } from "./geometry";
import { clamp01, easeInOutCubic, easeInQuint, easeOutQuint } from "./easing";
import { colorNum, motion } from "../theme/tokens";
import arcVert from "./shaders/arc.vert.glsl?raw";
import arcFrag from "./shaders/arc.frag.glsl?raw";

if (import.meta.hot) import.meta.hot.invalidate();

const POOL_SIZE = 180;
const TUBE_SEGMENTS = 72;
const RADIAL_SEGMENTS = 6;
/** Base tube radius in world units. Scales up with amount. */
const TUBE_BASE_RADIUS = 0.006;

type Slot = {
  mesh: THREE.Mesh;
  material: THREE.ShaderMaterial;
  startedAt: number;
  duration: number;
  active: boolean;
  onDone?: () => void;
};

/**
 * Pool of arcs rendered as tubes rising from the flat map plane.
 *
 * Geometry is rebuilt per dispatch (cheap: < 0.1ms for 72-segment tube).
 * Shader drives a luminance window along the tube in arc-length space.
 */
export class ArcLayer {
  readonly object: THREE.Group;
  private pool: Slot[] = [];
  private cursor = 0;

  constructor() {
    this.object = new THREE.Group();
    this.object.name = "arcs";
    this.object.renderOrder = 3;

    for (let i = 0; i < POOL_SIZE; i++) {
      const material = new THREE.ShaderMaterial({
        vertexShader: arcVert,
        fragmentShader: arcFrag,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        uniforms: {
          uHead: { value: 0 },
          uTrailLen: { value: 0.28 },
          uIntensity: { value: 0 },
          uHeadGlow: { value: 1.0 },
          uColor: { value: new THREE.Color(colorNum.flowPrimary) },
        },
      });
      const mesh = new THREE.Mesh(new THREE.BufferGeometry(), material);
      mesh.visible = false;
      mesh.frustumCulled = false;
      this.object.add(mesh);
      this.pool.push({
        mesh,
        material,
        startedAt: 0,
        duration: 0,
        active: false,
      });
    }
  }

  dispatch(ev: TransactionEvent, now: number, onDone?: (ev: TransactionEvent) => void) {
    const slot = this.pool[this.cursor];
    this.cursor = (this.cursor + 1) % POOL_SIZE;

    slot.mesh.geometry.dispose();
    const { curve, chord } = buildArcCurveFlat(ev.origin, ev.dest, TUBE_SEGMENTS);

    // Amount → bigness. $100 → 0, $1M → 1.
    const bigness = clamp01((Math.log10(ev.amountUsd + 1) - 2) / 4);
    const tubeRadius = TUBE_BASE_RADIUS * (1 + bigness * 2.2);

    const tube = new THREE.TubeGeometry(
      curve,
      TUBE_SEGMENTS,
      tubeRadius,
      RADIAL_SEGMENTS,
      false,
    );
    slot.mesh.geometry = tube;

    // Duration: long hauls get more time to feel the distance.
    const far = chord > 1.4;
    slot.startedAt = now;
    slot.duration = far ? motion.arcGlobalMs : motion.arcRegionalMs;
    slot.active = true;
    slot.mesh.visible = true;
    slot.onDone = onDone ? () => onDone(ev) : undefined;

    const failColor = new THREE.Color(colorNum.flowFail);
    // Per-product arc color. Card spend is cyan to read distinctly from
    // the gold send_money corridors; big amounts shift toward flowLarge
    // to keep the existing "brighter = larger" encoding.
    const cardColor = new THREE.Color(colorNum.productCard);
    const sendColor = new THREE.Color(colorNum.flowPrimary);
    const largeColor = new THREE.Color(colorNum.flowLarge);

    const baseForProduct = ev.product === "card" ? cardColor : sendColor;
    const color =
      ev.status === "reversed"
        ? failColor
        : baseForProduct.clone().lerp(largeColor, bigness * 0.6);

    slot.material.uniforms.uColor.value.copy(color);
    slot.material.uniforms.uTrailLen.value = 0.22 + 0.18 * bigness;
    slot.material.uniforms.uHeadGlow.value = 0.8 + 1.2 * bigness;
  }

  update(now: number) {
    for (const s of this.pool) {
      if (!s.active) continue;
      const t = clamp01((now - s.startedAt) / s.duration);
      s.material.uniforms.uHead.value = easeInOutCubic(t);

      const fadeIn = easeOutQuint(clamp01(t * 3.2));
      const fadeOut = 1 - easeInQuint(clamp01((t - 0.65) / 0.35));
      s.material.uniforms.uIntensity.value = fadeIn * fadeOut;

      if (t >= 1) {
        s.active = false;
        s.mesh.visible = false;
        s.onDone?.();
      }
    }
  }
}
